import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { NotesType } from '../../shared/enums/enum.enums';
import { IAccount } from '../../shared/models/account.models';
import {
  IHousehold, IInvestor, ITrustedContact, ISecurityAllocationPlan
} from '../../shared/models/investor.models';
import { IProposal } from '../../shared/models/proposal.models';
import { INote } from '../../shared/models/note.model';
import { IChartDataSource } from '../../shared/models/chart.models'

import { IAdvisor, IAdminMode } from '../../shared/models/advisor.models'; // Update the paths accordingly

@Injectable({
  providedIn: 'root',
})
export class AdvisorService {
  private readonly baseUrl = '/api/advisor';

  constructor(private http: HttpClient) { }

  private handleError(error: HttpErrorResponse): Observable<never> {
    console.error('An error occurred:', error);
    return throwError(() => new Error('Something went wrong; please try again later.'));
  }

  getAdminMode(): IAdminMode {
    try {
      // Check if "adminMode" exists in localStorage
      const adminModeString = localStorage.getItem("adminMode");
      if (adminModeString) {
        // Parse and return the stored object
        return JSON.parse(adminModeString) as IAdminMode;
      }
    } catch (error) {
      // Log the error for debugging purposes
      console.error("Error retrieving admin mode from localStorage:", error);
    }

    // Return a default value if no data is found or parsing fails
    return {} as IAdminMode;
  }

  // Get advisors
  getAdvisors(): Observable<IAdvisor[]> {
    return this.http.get<IAdvisor[]>(`${this.baseUrl}/getadvisors`).pipe(catchError(this.handleError));
  }

  getAdvisorById(advisorId: number): Observable<IAdvisor> {
    return this.http.get<IAdvisor>(`${this.baseUrl}/getadvisor/${advisorId}`).pipe(catchError(this.handleError));
  }

  // Get accounts
  getAccountsByInvestor(
    investorId: number,
    accountStatus: number,
    showClosedAccounts: boolean,
    withAUM: boolean,
    includeChildAccounts: boolean
  ): Observable<IAccount[]> {
    return this.http.get<IAccount[]>(
      `${this.baseUrl}/getaccountsbyinvestor/${investorId}/${accountStatus}/${showClosedAccounts}/${withAUM}/${includeChildAccounts}`
    ).pipe(catchError(this.handleError));
  }

  getAccountsByHousehold(
    householdId: number,
    accountStatus: number,
    showClosedAccounts: boolean,
    withAUM: boolean,
    includeChildAccounts: boolean
  ): Observable<IAccount[]> {
    return this.http.get<IAccount[]>(
      `${this.baseUrl}/getaccountsbyhousehold/${householdId}/${accountStatus}/${showClosedAccounts}/${withAUM}/${includeChildAccounts}`
    ).pipe(catchError(this.handleError));
  }

  // Households
  getHouseholds(withAUM: boolean, withAdvisors: boolean): Observable<IHousehold[]> {
    return this.http.get<IHousehold[]>(`${this.baseUrl}/gethouseholds/${withAUM}/${withAdvisors}`).pipe(catchError(this.handleError));
  }

  getHouseholdsByInvestor(investorId: number): Observable<IHousehold[]> {
    return this.http.get<IHousehold[]>(`${this.baseUrl}/gethouseholdsbyinvestor/${investorId}`).pipe(catchError(this.handleError));
  }

  createUpdateHousehold(household: IHousehold): Observable<IHousehold> {
    return this.http.post<IHousehold>(`${this.baseUrl}/createupdatehousehold`, household).pipe(catchError(this.handleError));
  }

  // Notes
  createNote(note: INote, type: NotesType): Observable<INote> {
    const endpoint = type === NotesType.INVESTOR ? 'addnoteforinvestor' : 'addnoteforhousehold';
    return this.http.post<INote>(`${this.baseUrl}/${endpoint}`, note).pipe(catchError(this.handleError));
  }

  updateNote(note: INote, type: NotesType): Observable<INote> {
    const endpoint = type === NotesType.INVESTOR ? 'updatenoteforinvestor' : 'updatenoteforhousehold';
    return this.http.post<INote>(`${this.baseUrl}/${endpoint}`, note).pipe(catchError(this.handleError));
  }

  deleteNote(noteId: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/deletenote/${noteId}`).pipe(catchError(this.handleError));
  }

  // Proposals
  getProposalsByTitle(titleId: number): Observable<IProposal[]> {
    return this.http.get<IProposal[]>(`${this.baseUrl}/getproposalsbytitle/${titleId}`).pipe(catchError(this.handleError));
  }

  createOrUpdateProposal(proposal: IProposal): Observable<IProposal> {
    return this.http.post<IProposal>(`${this.baseUrl}/createproposal`, proposal).pipe(catchError(this.handleError));
  }

  // Security Allocation Plans
  getSecurityAllocationPlans(advisoryFirmId: number): Observable<ISecurityAllocationPlan[]> {
    return this.http.get<ISecurityAllocationPlan[]>(`${this.baseUrl}/getsecurityallocationplans/${advisoryFirmId}`).pipe(catchError(this.handleError));
  }

  // Trusted Contacts
  getTrustedContactsForInvestor(investorId: number): Observable<ITrustedContact[]> {
    return this.http.get<ITrustedContact[]>(`${this.baseUrl}/gettrustedcontactsforinvestor/${investorId}`).pipe(catchError(this.handleError));
  }

  saveTrustedContacts(investorId: number, contacts: ITrustedContact[]): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/saveinvestortrustedcontacts/${investorId}`, contacts).pipe(catchError(this.handleError));
  }

  // Charts
  getMarketValueByInvestor(investorId: number): Observable<IChartDataSource> {
    return this.http.get<IChartDataSource>(`${this.baseUrl}/getmarketvaluebyinvestor/${investorId}`).pipe(catchError(this.handleError));
  }

  getMarketValueByHousehold(householdId: number): Observable<IChartDataSource> {
    return this.http.get<IChartDataSource>(`${this.baseUrl}/getmarketvaluebyhousehold/${householdId}`).pipe(catchError(this.handleError));
  }

  // Miscellaneous
  searchInvestors(searchTerm: string): Observable<IInvestor[]> {
    return this.http.get<IInvestor[]>(`${this.baseUrl}/searchInvestors/${encodeURIComponent(searchTerm)}`).pipe(catchError(this.handleError));
  }

  searchHouseholds(searchTerm: string): Observable<IHousehold[]> {
    return this.http.get<IHousehold[]>(`${this.baseUrl}/searchhousehold/${encodeURIComponent(searchTerm)}`).pipe(catchError(this.handleError));
  }

  searchAccounts(searchTerm: string): Observable<IAccount[]> {
    return this.http.get<IAccount[]>(`${this.baseUrl}/searchaccounts/${encodeURIComponent(searchTerm)}`).pipe(catchError(this.handleError));
  }
}
